import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { PublicLayout } from './shared/layouts/PublicLayout';
import { LandingPage } from './features/landing/pages/LandingPage';
import { CookieConsent } from './shared/components/CookieConsent/CookieConsent';
import { initTracking } from './shared/utils/tracking';

const BlogPage = lazy(() => import('./features/blog/pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('./features/blog/pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const PrivacyPage = lazy(() => import('./features/legal/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./features/legal/pages/TermsPage').then(m => ({ default: m.TermsPage })));
const DisputesPage = lazy(() => import('./features/legal/pages/DisputesPage').then(m => ({ default: m.DisputesPage })));
const RefundsPage = lazy(() => import('./features/legal/pages/RefundsPage').then(m => ({ default: m.RefundsPage })));
const AboutPage = lazy(() => import('./features/legal/pages/AboutPage').then(m => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('./features/legal/pages/ContactPage').then(m => ({ default: m.ContactPage })));
const CookiesPage = lazy(() => import('./features/legal/pages/CookiesPage').then(m => ({ default: m.CookiesPage })));
const NotFoundPage = lazy(() => import('./features/errors/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('./features/errors/pages/ForbiddenPage'));
const ServerErrorPage = lazy(() => import('./features/errors/pages/ServerErrorPage'));
const SessionExpiredPage = lazy(() => import('./features/errors/pages/SessionExpiredPage'));
const RateLimitPage = lazy(() => import('./features/errors/pages/RateLimitPage'));
const ServiceUnavailablePage = lazy(() => import('./features/errors/pages/ServiceUnavailablePage'));
const ErrorBoundary = lazy(() => import('./features/errors/components/ErrorBoundary'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #E5DCDA', borderTopColor: '#85001B', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function App() {
  useEffect(() => { initTracking() }, [])

  return (
    <ErrorBoundary>
      <BrowserRouter basename="/">
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Error pages — outside layout */}
              <Route path="/403" element={<ForbiddenPage />} />
              <Route path="/500" element={<ServerErrorPage />} />
              <Route path="/419" element={<SessionExpiredPage />} />
              <Route path="/429" element={<RateLimitPage />} />
              <Route path="/503" element={<ServiceUnavailablePage />} />

              {/* Public site — inside layout */}
              <Route element={<PublicLayout />}>
                <Route index element={<LandingPage />} />

                {/* Blog */}
                <Route path="blog" element={<BlogPage />} />
                <Route path="blog/:slug" element={<BlogPostPage />} />

                {/* Legal */}
                <Route path="privacy" element={<PrivacyPage />} />
                <Route path="terms" element={<TermsPage />} />
                <Route path="disputes" element={<DisputesPage />} />
                <Route path="refunds" element={<RefundsPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="cookies" element={<CookiesPage />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <CookieConsent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
